import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@bri/shared-components'
import { useWorkspace } from '@/lib/contexts/useWorkspace'
import { completeWorkspaceOnboarding } from '@/lib/onboarding/workspaceOnboarding'
import { describeWorkspaceRole, formatWorkspaceRole, type WorkspaceRole } from '@/lib/workspaceRoles'
import {
  ArrowRightIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  FolderIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'

type StepDefinition = {
  title: string
  description: string
  bullets: string[]
  icon: typeof BuildingOfficeIcon
}

export default function OnboardingPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { currentWorkspace, workspaces } = useWorkspace()
  const [stepIndex, setStepIndex] = useState(0)

  const returnTo = typeof location.state?.from === 'string' ? location.state.from : '/app'

  const steps = useMemo<StepDefinition[]>(() => {
    const activeWorkspaceName = currentWorkspace?.name || 'your active workspace'

    return [
      {
        title: 'Workspaces are your top-level home',
        description: 'Use a workspace for a client, department, or internal group. It is the main container people enter before they start project work.',
        bullets: [
          'You can belong to more than one workspace.',
          'The workspace in the header is the one you are currently viewing.',
          'Workspace settings and member access are managed at this level.',
        ],
        icon: BuildingOfficeIcon,
      },
      {
        title: 'Projects live inside a workspace',
        description: 'Every project belongs to one workspace. When you switch workspaces, you switch the set of projects you are looking at.',
        bullets: [
          `Projects, files, and results are scoped to ${activeWorkspaceName}.`,
          'Create a new workspace when the work should be kept separate.',
          'Use projects for individual studies, deliverables, or analysis efforts.',
        ],
        icon: FolderIcon,
      },
      {
        title: 'Members access the workspace together',
        description: 'Members are the people who can view or work inside a workspace. This replaces the older team terminology in the app.',
        bullets: (['owner', 'editor', 'viewer'] as WorkspaceRole[]).map(
          (role) => `${formatWorkspaceRole(role)}: ${describeWorkspaceRole(role)}`
        ),
        icon: UsersIcon,
      },
      {
        title: workspaces.length > 0 ? 'You are ready to get started' : 'Start by creating your first workspace',
        description: workspaces.length > 0
          ? 'From here you can manage workspaces, create projects, and invite members without losing track of where that work belongs.'
          : 'Once you create a workspace, you can add projects and invite members into that shared space.',
        bullets: workspaces.length > 0
          ? [
              'Use the header to switch workspaces at any time.',
              'Open Projects to work inside the current workspace.',
              'Open Members when you need to grant or change access.',
            ]
          : [
              'Create a workspace for your client, department, or team.',
              'Add projects inside that workspace once it exists.',
              'Invite members after you are ready to collaborate.',
            ],
        icon: CheckCircleIcon,
      },
    ]
  }, [currentWorkspace?.name, workspaces.length])

  const currentStep = steps[stepIndex]
  const isLastStep = stepIndex === steps.length - 1

  const finishOnboarding = (destination: string) => {
    completeWorkspaceOnboarding(user?.email)
    navigate(destination, { replace: true })
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-10 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-teal-300">Bellomy Insights</p>
            <h1 className="mt-3 text-3xl font-semibold sm:text-4xl">Understand the Workspace Hierarchy</h1>
            <p className="mt-3 max-w-2xl text-sm text-slate-300 sm:text-base">
              A quick guide to how workspaces, projects, and members fit together.
            </p>
          </div>

          <button
            type="button"
            onClick={() => finishOnboarding(returnTo)}
            className="rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-white/30 hover:bg-white/5"
          >
            Skip for now
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <p className="text-sm font-medium text-slate-200">Guide steps</p>
            <div className="mt-4 space-y-3">
              {steps.map((step, index) => (
                <button
                  key={step.title}
                  type="button"
                  onClick={() => setStepIndex(index)}
                  className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                    index === stepIndex
                      ? 'border-teal-400 bg-teal-400/10 text-white'
                      : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10'
                  }`}
                >
                  <div className="text-xs uppercase tracking-wide text-slate-400">Step {index + 1}</div>
                  <div className="mt-1 text-sm font-medium">{step.title}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white p-8 text-slate-900 shadow-2xl shadow-slate-950/20">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="inline-flex rounded-full bg-teal-50 p-3 text-teal-700">
                  <currentStep.icon className="h-6 w-6" />
                </div>
                <h2 className="mt-6 text-2xl font-semibold">{currentStep.title}</h2>
                <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">{currentStep.description}</p>
              </div>

              <div className="shrink-0 whitespace-nowrap rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">
                {stepIndex + 1} / {steps.length}
              </div>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-1">
              {currentStep.bullets.map((bullet) => (
                <div key={bullet} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <p className="text-sm leading-6 text-slate-700">{bullet}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-6">
              <button
                type="button"
                onClick={() => setStepIndex((current) => Math.max(current - 1, 0))}
                disabled={stepIndex === 0}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Back
              </button>

              <div className="flex flex-wrap items-center gap-3">
                {isLastStep ? (
                  <>
                    <button
                      type="button"
                      onClick={() => finishOnboarding(workspaces.length > 0 ? '/app/projects' : '/app/workspaces/new')}
                      className="inline-flex items-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
                    >
                      {workspaces.length > 0 ? 'Go to Projects' : 'Create Workspace'}
                      <ArrowRightIcon className="ml-2 h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => finishOnboarding('/app/workspaces')}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                    >
                      View Workspaces
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setStepIndex((current) => Math.min(current + 1, steps.length - 1))}
                    className="inline-flex items-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
                  >
                    Next
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
